import { useUnsavedChanges } from "../../layout/AppShellContext";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useActiveCompanyId } from "../../api/useActiveCompanyId";
import { Cancellation } from "../OperationsDayPage/operationsDay";
import { api } from "../../api/client";
import { useActiveCompanyIdApi } from "../../api/useActiveCompanyIdApi";
import { MenuHeader } from "../Dashboard/block/MenuHeader/MenuHeader";
import { MobileMenu } from "../Dashboard/MobileMenu";
import "../shared/mobileSection.scss";
import { getRecordGatewayErrorMessage } from "./recordGatewayError";
import "./RecordsPage.scss";

type ServiceItem = {
    id: number;
    title: string;
    price?: number | null;
    length?: number | null;
    priceDisplay?: string;
    lengthDisplay?: string;
    staffIds: number[];
    staffAvailability?: Record<string, {
        available: boolean;
        source: string;
        reasonCode: string;
        reasonMessage: string;
    }>;
};

type ServiceGroupItem = {
    id: number;
    title: string;
    services: ServiceItem[];
};

type StaffItem = {
    id: number;
    name: string;
    isCurrent: boolean;
    specializations: string[];
    serviceIds: number[];
};

type CompanyItem = {
    id: number;
    title: string;
    address: string;
    city: string;
    ownStaffId: number | null;
    staff: StaffItem[];
    serviceGroups: ServiceGroupItem[];
};

type BootstrapResponse = {
    defaultCompanyId: number | null;
    defaultStaffId: number | null;
    companies: CompanyItem[];
};

type CreateRecordResponse = {
    success?: boolean;
    message?: string;
    booking?: {
        id: number;
        companyId: number;
        serviceIds: number[];
        resourceId: number;
        datetime: string;
    };
    staff?: {
        id: number;
        name: string;
    };
};

type LastCreatedSlot = {
    resourceId: number;
    datetime: string;
};

type SlotConflictDetails = {
    type?: "record" | "transaction";
    recordId?: number | null;
    transactionId?: number | null;
    dateTime?: string | null;
    endDateTime?: string | null;
    timeRange?: string | null;
    staff?: {
        id?: number | null;
        name?: string | null;
    } | null;
    client?: {
        id?: number | null;
        name?: string | null;
        phone?: string | null;
    } | null;
    serviceTitle?: string | null;
};

type RequestedSlotDetails = {
    dateTime?: string | null;
    endDateTime?: string | null;
    timeRange?: string | null;
    durationMinutes?: number | null;
};

function normalizePhone(phone: string): string {
    const digits = String(phone || "").replace(/\D+/g, "");
    if (digits.length === 11 && digits.charAt(0) === "8") {
        return `7${digits.slice(1)}`;
    }

    return digits;
}

function getDefaultStaffId(company: CompanyItem | undefined, preferredStaffId: number): number {
    if (!company) {
        return 0;
    }

    if (preferredStaffId > 0) {
        const preferred = company.staff.find((staff) => staff.id === preferredStaffId);
        if (preferred) {
            return preferred.id;
        }
    }

    if (company.ownStaffId) {
        return company.ownStaffId;
    }

    if (company.staff.length > 1) {
        return 1;
    }

    return company.staff[0]?.id ?? 0;
}

function findService(company: CompanyItem | undefined, serviceId: number): ServiceItem | undefined {
    if (!company) {
        return undefined;
    }

    for (const group of company.serviceGroups) {
        const service = group.services.find((item) => item.id === serviceId);
        if (service) {
            return service;
        }
    }

    return undefined;
}

function formatApiDate(value?: string | null): string {
    const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) {
        return "";
    }

    return `${match[3]}.${match[2]}.${match[1]}`;
}

function formatClientLabel(client?: SlotConflictDetails["client"]): string {
    if (!client) {
        return "";
    }

    const parts = [client.name, client.phone].filter(Boolean);
    return parts.join(", ");
}

function buildSlotConflictMessage(details: any): string | null {
    if (!details || details.error !== "slot_conflict") {
        return null;
    }

    const requested = details.requestedSlot as RequestedSlotDetails | undefined;
    const conflicts = Array.isArray(details.conflicts) ? details.conflicts : [];
    const conflict = (details.conflict || conflicts[0]) as SlotConflictDetails | undefined;
    const lines = ["Выбранное время уже занято."];

    if (requested?.dateTime || requested?.timeRange) {
        const date = formatApiDate(requested.dateTime);
        const range = requested.timeRange || "";
        lines.push(`Выбранный интервал: ${[date, range].filter(Boolean).join(", ")}.`);
    }

    if (conflict) {
        const conflictKind = conflict.type === "transaction" ? "операцией" : "записью";
        const conflictId = conflict.type === "transaction"
            ? conflict.transactionId
            : conflict.recordId;
        const conflictLabel = conflictId ? `${conflictKind} #${conflictId}` : conflictKind;
        const conflictDate = formatApiDate(conflict.dateTime);
        const conflictRange = conflict.timeRange || "";

        lines.push(
            `Пересечение с ${conflictLabel}: ${[conflictDate, conflictRange].filter(Boolean).join(", ")}.`
        );

        if (conflict.staff?.name) {
            lines.push(`Мастер: ${conflict.staff.name}.`);
        }

        const clientLabel = formatClientLabel(conflict.client);
        if (clientLabel) {
            lines.push(`Клиент: ${clientLabel}.`);
        }

        if (conflict.serviceTitle) {
            lines.push(`Услуга: ${conflict.serviceTitle}.`);
        }
    } else if (typeof details.message === "string" && details.message.trim()) {
        lines.push(details.message.trim());
    }

    if (conflicts.length > 1) {
        lines.push(`Есть ещё пересечения: ${conflicts.length - 1}.`);
    }

    return lines.join("\n");
}

function getErrorMessage(error: any, fallback: string): string {
    const details = error?.details;
    const slotConflictMessage = buildSlotConflictMessage(details);
    if (slotConflictMessage) {
        return slotConflictMessage;
    }

    if (typeof details?.message === "string" && details.message.trim()) {
        return details.message;
    }

    const gatewayErrorMessage = getRecordGatewayErrorMessage(details?.error);
    if (gatewayErrorMessage) {
        return gatewayErrorMessage;
    }

    if (typeof details?.error === "string" && details.error.trim()) {
        return details.error;
    }

    if (typeof details === "string" && details.trim()) {
        return details;
    }

    return error?.message ?? fallback;
}

export const RecordCreatePage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const operationParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const fromOperations = operationParams.get("from") === "operations-day";
    const { value: activeCompanyId } = useActiveCompanyId();
    const bootstrapRequest = useRef<AbortController | null>(null);
    const { value: activeCompanyIdApi, resolved: activeCompanyApiResolved } = useActiveCompanyIdApi();
    const [menuOpen, setMenuOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [bootstrap, setBootstrap] = useState<BootstrapResponse | null>(null);
    const [selectedCompanyId, setSelectedCompanyId] = useState(0);
    const [selectedStaffId, setSelectedStaffId] = useState(0);
    const [selectedServiceIds, setSelectedServiceIds] = useState<number[]>([]);
    const [clientPhone, setClientPhone] = useState("");
    const [clientName, setClientName] = useState("");
    const [recordDate, setRecordDate] = useState("");
    const [recordTime, setRecordTime] = useState("");
    const [comment, setComment] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [createdBooking, setCreatedBooking] = useState<CreateRecordResponse["booking"] | null>(null);
    useUnsavedChanges(submitting || !!clientPhone.trim() || !!clientName.trim() || !!comment.trim() || selectedServiceIds.length > 0, "Новая запись");
    const [lastCreatedSlot, setLastCreatedSlot] = useState<LastCreatedSlot | null>(null);

    const selectedCompany = useMemo(() => {
        return bootstrap?.companies.find((company) => company.id === selectedCompanyId);
    }, [bootstrap, selectedCompanyId]);

    const selectedStaff = useMemo(() => {
        return selectedCompany?.staff.find((staff) => staff.id === selectedStaffId);
    }, [selectedCompany, selectedStaffId]);

    const selectedServicesSummary = useMemo(() => {
        const selectedServices: ServiceItem[] = [];
        let totalLength = 0;

        selectedServiceIds.forEach((serviceId) => {
            const service = findService(selectedCompany, serviceId);
            if (!service) {
                return;
            }

            selectedServices.push(service);
            totalLength += Number(service.length || 0);
        });

        return { selectedServices, totalLength };
    }, [selectedCompany, selectedServiceIds]);

    const canUseAnyMaster = Boolean(selectedCompany && selectedCompany.staff.length > 1);

    const loadBootstrap = async () => {
        if (!activeCompanyIdApi) {
            return;
        }

        bootstrapRequest.current?.abort();
        const controller = new AbortController();
        bootstrapRequest.current = controller;
        try {
            setLoading(true); setSubmitting(false); setSelectedServiceIds([]);
            setLoadError(null);
            setSubmitError(null);
            setSuccessMessage(null);
            setCreatedBooking(null);
            setLastCreatedSlot(null);
            setBootstrap(null);
            setClientName(""); setClientPhone(""); setComment(""); setRecordDate(""); setRecordTime("");
            if (fromOperations && operationParams.get("operationCompanyId") !== activeCompanyId) {
                throw new Error("Выбран другой салон. Вернитесь к исходному списку и выберите клиента заново.");
            }

            const response = await api.get<BootstrapResponse>("/api/gateway/records/create/bootstrap", {
                companyIdApi: activeCompanyIdApi,
            }, {
                timeout: 25000,
                signal: controller.signal,
            });
            if (controller.signal.aborted) return;
            const companies = Array.isArray(response?.companies) ? response.companies : [];
            const nextCompanyId = response?.defaultCompanyId || companies[0]?.id || 0;
            const nextCompany = companies.find((company) => company.id === nextCompanyId);
            const nextStaffId = getDefaultStaffId(nextCompany, response?.defaultStaffId || 0);

            setBootstrap({ ...response, companies });
            setSelectedCompanyId(nextCompanyId);
            setSelectedStaffId(nextStaffId);
            setSelectedServiceIds([]);
            if (fromOperations) {
                const cancellationId = operationParams.get("cancellationId");
                let candidate: Pick<Cancellation, "clientName" | "clientPhone" | "staffId" | "services"> | undefined;
                if (cancellationId) {
                    const context = await api.get<{ items: Cancellation[] }>("/api/operations-day/cancellations", { recordId: cancellationId }, { signal: controller.signal });
                    if (controller.signal.aborted) return;
                    candidate = context.items[0];
                    if (!candidate?.clientPhone) throw new Error("Нет доступа к телефону клиента или отмена больше не доступна.");
                }
                const wantedStaff = Number(operationParams.get("staffId")) || candidate?.staffId || nextStaffId;
                const targetStaff = nextCompany?.staff.find(staff => staff.id === wantedStaff);
                setSelectedStaffId(targetStaff?.id || nextStaffId);
                setClientName(candidate?.clientName || "");
                setClientPhone(candidate?.clientPhone || "");
                setSelectedServiceIds((candidate?.services || []).map(service => service.id).filter(id => Boolean(findService(nextCompany, id))));
                const date = operationParams.get("date") || "";
                const time = operationParams.get("time") || "";
                setRecordDate(/^\d{4}-\d{2}-\d{2}$/.test(date) ? date : "");
                setRecordTime(/^([01]\d|2[0-3]):[0-5]\d$/.test(time) ? time : "");
            }
        } catch (error: any) {
            if (!controller.signal.aborted) { setBootstrap(null); setLoadError(getErrorMessage(error, "Не удалось загрузить форму записи")); }
        } finally {
            if (!controller.signal.aborted) setLoading(false);
        }
    };

    useEffect(() => {
        if (!activeCompanyApiResolved) {
            return;
        }

        if (!activeCompanyIdApi) {
            setBootstrap(null);
            setLoadError("Для выбранной точки записи пока не подключены.");
            return;
        }

        void loadBootstrap();
        return () => bootstrapRequest.current?.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeCompanyApiResolved, activeCompanyIdApi, activeCompanyId, location.search]);

    const resetTransientState = () => {
        setSubmitError(null);
        setSuccessMessage(null);
        setCreatedBooking(null);
    };

    const handleCompanyChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const nextCompanyId = Number(event.target.value) || 0;
        const nextCompany = bootstrap?.companies.find((company) => company.id === nextCompanyId);
        const nextStaffId = getDefaultStaffId(nextCompany, 0);

        setSelectedCompanyId(nextCompanyId);
        setSelectedStaffId(nextStaffId);
        setSelectedServiceIds([]);
        setLastCreatedSlot(null);
        resetTransientState();
    };

    const handleStaffChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const nextStaffId = Number(event.target.value) || 0;
        setSelectedStaffId(nextStaffId);
        setLastCreatedSlot(null);
        resetTransientState();
    };

    const toggleService = (serviceId: number) => {
        const service = findService(selectedCompany, serviceId);
        if (!service) {
            return;
        }

        setSelectedServiceIds((current) => (
            current.includes(serviceId)
                ? current.filter((id) => id !== serviceId)
                : [...current, serviceId]
        ));
        resetTransientState();
    };

    const isCurrentSlotAlreadyCreated = (): boolean => {
        if (!lastCreatedSlot || selectedStaffId === 1 || !recordDate || !recordTime) {
            return false;
        }

        return lastCreatedSlot.resourceId === selectedStaffId &&
            lastCreatedSlot.datetime === `${recordDate} ${recordTime}`;
    };

    const handleSubmit = async () => {
        const context = bootstrapRequest.current;
        const stillCurrent = () => context === bootstrapRequest.current && !context?.signal.aborted;
        const clientNameTrimmed = clientName.trim();
        const clientPhoneTrimmed = clientPhone.trim();
        const commentTrimmed = comment.trim();
        const serviceIdsForSubmit = Array.from(new Set(selectedServiceIds));

        if (!selectedCompany || selectedCompanyId <= 0) {
            setSubmitError("Выберите филиал.");
            return;
        }

        if (selectedStaffId <= 0) {
            setSubmitError("Выберите мастера.");
            return;
        }

        if (!serviceIdsForSubmit.length) {
            setSubmitError("Выберите хотя бы одну услугу.");
            return;
        }

        if (!recordDate || !recordTime) {
            setSubmitError("Выберите дату и время записи.");
            return;
        }

        if (!clientNameTrimmed) {
            setSubmitError("Укажите имя клиента.");
            return;
        }

        if (normalizePhone(clientPhoneTrimmed).length !== 11) {
            setSubmitError("Введите корректный номер телефона клиента.");
            return;
        }

        if (isCurrentSlotAlreadyCreated()) {
            setSubmitError("На это время уже создана запись. Выберите другое время.");
            return;
        }

        try {
            setSubmitting(true);
            setSubmitError(null);
            setSuccessMessage(null);
            setCreatedBooking(null);

            const response = await api.post<CreateRecordResponse>(
                "/api/gateway/records/create",
                {
                    companyIdApi: activeCompanyIdApi ? Number(activeCompanyIdApi) : undefined,
                    companyId: selectedCompanyId,
                    booking: {
                        companyId: selectedCompanyId,
                        resourceId: selectedStaffId,
                        serviceIds: serviceIdsForSubmit,
                        datetime: `${recordDate} ${recordTime}`,
                    },
                    client: {
                        phone: clientPhoneTrimmed,
                        name: clientNameTrimmed,
                    },
                    commentRecord: commentTrimmed,
                },
                { timeout: 25000, headers: { "X-Company-Id": activeCompanyId || "" } },
            );
            if (!stillCurrent()) return;

            const booking = response?.booking || null;
            setSuccessMessage(response?.message || "Запись создана.");
            setCreatedBooking(booking);
            if (booking) {
                setLastCreatedSlot({
                    resourceId: booking.resourceId,
                    datetime: booking.datetime,
                });
            }
            setClientName("");
            setClientPhone("");
            setComment("");
            setSelectedServiceIds([]);
            if (fromOperations && booking) window.setTimeout(() => { if (stillCurrent()) navigate("/operations-day"); }, 100);
        } catch (error: any) {
            if (stillCurrent()) setSubmitError(getErrorMessage(error, "Не удалось создать запись"));
        } finally {
            if (stillCurrent()) setSubmitting(false);
        }
    };

    const renderServices = () => {
        if (!selectedCompany) {
            return null;
        }

        if (selectedCompany.serviceGroups.length === 0) {
            return <div className="section-empty">Для этого филиала пока не найдено активных услуг.</div>;
        }

        return selectedCompany.serviceGroups.map((group) => (
            <div className="record-create-services-group" key={group.id}>
                <div className="record-create-group-title">{group.title}</div>
                <div className="record-create-services-list">
                    {group.services.map((service) => {
                        const checked = selectedServiceIds.includes(service.id);
                        const label = checked ? "Выбрано" : "Выбрать";

                        return (
                            <button
                                type="button"
                                key={service.id}
                                className={`record-create-service${checked ? " active" : ""}`}
                                aria-pressed={checked}
                                onClick={() => toggleService(service.id)}
                                disabled={submitting}
                            >
                                <span className="record-create-service-main">
                                    <span className="record-create-service-title">{service.title}</span>
                                    <span className="record-create-service-meta">
                                        {service.lengthDisplay ? `${service.lengthDisplay} мин` : "Длительность по салону"}
                                        {service.priceDisplay ? ` · ${service.priceDisplay} р` : ""}
                                    </span>
                                </span>
                                <span className="record-create-service-state">{label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>
        ));
    };

    return (
        <div className="section-page records-page record-create-page">
            <MenuHeader onMenuClick={() => setMenuOpen(true)} />
            <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />

            <div className="record-create-title-row">
                <h1 className="section-title">Новая запись</h1>
                <button type="button" className="section-btn-secondary record-create-back" onClick={() => navigate(fromOperations ? "/operations-day" : "/records")}>
                    {fromOperations ? "К операционному дню" : "К записям"}
                </button>
            </div>

            <div className="section-card record-create-hint">
                <div className="section-subtitle">Записать клиента</div>
                <div className="section-item-text">
                    Подтверждение по коду не требуется. Достаточно имени и номера телефона клиента.
                </div>
            </div>

            {loading ? <div className="section-empty">Загрузка формы записи...</div> : null}
            {loadError ? (
                <div className="section-error">
                    {loadError}
                    {activeCompanyIdApi ? (
                        <div className="section-actions record-create-retry">
                            <button type="button" className="section-btn" onClick={() => void loadBootstrap()}>
                                Обновить
                            </button>
                        </div>
                    ) : null}
                </div>
            ) : null}

            {!loading && !loadError && bootstrap && bootstrap.companies.length === 0 ? (
                <div className="section-empty">Для вашей учётной записи пока не найдено доступных филиалов.</div>
            ) : null}

            {!loading && !loadError && bootstrap && bootstrap.companies.length > 0 ? (
                <>
                    <div className="section-card">
                        <div className="section-subtitle">Параметры записи</div>
                        <div className="section-form">
                            <div className="section-field">
                                <div className="section-label">Филиал</div>
                                <select className="section-select" value={selectedCompanyId} onChange={handleCompanyChange}>
                                    <option value={0}>Выберите филиал</option>
                                    {bootstrap.companies.map((company) => (
                                        <option key={company.id} value={company.id}>
                                            {company.city ? `${company.city}, ` : ""}{company.address || company.title}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="section-field">
                                <div className="section-label">Мастер</div>
                                <select className="section-select" value={selectedStaffId} onChange={handleStaffChange}>
                                    <option value={0}>Выберите мастера</option>
                                    {canUseAnyMaster ? <option value={1}>Любой подходящий мастер</option> : null}
                                    {selectedCompany?.staff.map((staff) => (
                                        <option key={staff.id} value={staff.id}>
                                            {staff.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {selectedStaff && selectedStaff.specializations.length ? (
                                <div className="record-create-note">
                                    Специализации: {selectedStaff.specializations.join(", ")}
                                </div>
                            ) : null}

                            <div className="section-inline">
                                <div className="section-field">
                                    <div className="section-label">Дата</div>
                                    <input className="section-input" type="date" value={recordDate} onChange={(event) => {
                                        setRecordDate(event.target.value);
                                        setLastCreatedSlot(null);
                                        resetTransientState();
                                    }} />
                                </div>
                                <div className="section-field">
                                    <div className="section-label">Время</div>
                                    <input className="section-input" type="time" value={recordTime} onChange={(event) => {
                                        setRecordTime(event.target.value);
                                        setLastCreatedSlot(null);
                                        resetTransientState();
                                    }} />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="section-card">
                        <div className="section-subtitle">Клиент</div>
                        <div className="section-form">
                            <div className="section-field">
                                <div className="section-label">Имя клиента</div>
                                <input className="section-input" type="text" placeholder="Например, Анна" value={clientName} onChange={(event) => {
                                    setClientName(event.target.value);
                                    resetTransientState();
                                }} />
                            </div>
                            <div className="section-field">
                                <div className="section-label">Номер телефона клиента</div>
                                <input className="section-input" type="tel" placeholder="+7 999 123-45-67" value={clientPhone} onChange={(event) => {
                                    setClientPhone(event.target.value);
                                    resetTransientState();
                                }} />
                            </div>
                            <div className="section-field">
                                <div className="section-label">Комментарий</div>
                                <textarea className="section-textarea" placeholder="Комментарий к записи" value={comment} onChange={(event) => {
                                    setComment(event.target.value);
                                    resetTransientState();
                                }} />
                            </div>
                        </div>
                    </div>

                    <div className="section-card">
                        <div className="section-subtitle">Услуги</div>
                        {renderServices()}
                    </div>

                    <div className="section-card">
                        <div className="section-subtitle">Что попадёт в запись</div>
                        {selectedServicesSummary.selectedServices.length === 0 ? (
                            <div className="section-empty record-create-empty-inner">Выберите услуги, которые клиент хочет получить.</div>
                        ) : (
                            <>
                                <div className="record-create-summary-row">
                                    <span>Выбрано услуг</span>
                                    <strong>{selectedServicesSummary.selectedServices.length}</strong>
                                </div>
                                <div className="record-create-summary-row">
                                    <span>Общая длительность</span>
                                    <strong>{selectedServicesSummary.totalLength || 0} мин</strong>
                                </div>
                                <div className="record-create-selected-list">
                                    {selectedServicesSummary.selectedServices.map((service) => (
                                        <div className="record-create-selected-service" key={service.id}>
                                            <span>{service.title}</span>
                                            <button type="button" onClick={() => toggleService(service.id)}>Убрать</button>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>

                    {submitError ? <div className="section-error record-create-submit-error">{submitError}</div> : null}

                    {createdBooking ? (
                        <div className="section-success">
                            <strong>Клиент записан.</strong>
                            <div>Дата и время: {createdBooking.datetime}</div>
                            {successMessage ? <div>{successMessage}</div> : null}
                        </div>
                    ) : null}

                    <button type="button" className="section-btn record-create-submit" disabled={submitting} onClick={() => void handleSubmit()}>
                        {submitting ? "Сохраняем запись..." : "Записать клиента"}
                    </button>
                </>
            ) : null}
        </div>
    );
};

export default RecordCreatePage;
